<template>
  <div class="index-panel">
    <div class="index-panel__search">
      <SearchInput
        v-model="search"
        :modifiers="['radius-medium-screen']"
        :placeholder="$t('general.search')"
      />
    </div>
    <div :class="{ 'index-panel__group': true, 'index-panel__group--margin-right': !showFilter }">
      <Select
        v-if="sortByOptions"
        v-model="sortBy"
        :items="sortByOptions"
        :modifiers="['base-label']"
        :placeholder="$t('general.sort_by')"
        placement="bottom-start"
      />
      <div
        v-if="showFilter"
        class="index-panel__border"
      />
      <Filter
        v-if="showFilter && (isActiveFilter || modelValue?.length > 0)"
        :conditionFilter="false"
        :mineOptions="filterByOptions?.mine"
        :modifiers="['normal-font-weight', !isActiveFilter ? 'grey' : '']"
        :priorityFilter="false"
        :processOptions="filterByOptions?.process"
        :typeFilter="false"
        :typeOptions="filterByOptions?.type"
        @set-filter-options="setFilterOptions"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, reactive, computed } from 'vue';
import I18n from 'i18n-js';
import { useActiveFilter } from '@/functions/composables/active_filter.js';
import orderBy from '@/functions/order_by';

// Components
import SearchInput from '@/components/form/search_input.vue';
import Searcher from '@/functions/searcher';
import Select from '@/components/form/select.vue';
import Filter from '@/components/issues/issue_filter.vue';

const props = defineProps({
  /** Division data before search or filtering occurs */
  initialData: { type: Array, required: true, default: () => [] },
  /** Model value */
  modelValue: { type: Array, required: true, default: () => [] },
  /** The operation type. Can only be `shaft`, `shafts_v2_shafts`, `plant`, `ship`, `equipment` at the moment. */
  operationType: {
    type: String,
    required: true,
    default: 'plants',
    validator: (value) => ['plants', 'shafts', 'shafts_v2_shafts', 'ships', 'equipments'].includes(value),
  },
  /** Filter data */
  filterByOptions: { type: Object, required: false, default: () => {} },
});

const { isActiveFilter, validateFilterOptions } = useActiveFilter();
const search = ref(null);
const sortBy = ref(null);
const mines = ref([]);
const processes = ref([]);
const types = ref([]);
const searcher = reactive([]);
const searcherList = ref([[]]);
let searchKeys = ['name'];
const byName = orderBy('name', x => x.toLowerCase());

const isShaft = computed(() => props.operationType === 'shafts');
const isEquipment = computed(() => props.operationType === 'equipments');
const showFilter = computed(() => props.filterByOptions && Object.keys(props.filterByOptions).length > 0);

/**
 * Reloads the list of operations when filtering occurs
 */
watch(
  () => [mines.value, processes.value, types.value],
  () => {
    filteredOperationList();
  }
);

function genericSortOption(value) {
  return {
    label: I18n.t(`general.${value}`),
    value: value,
  };
}

const sortByOptions = [
  ...['alphabetical', 'alphabetical_reverse'].map(genericSortOption),
];

function filterItems(list) {
  return list?.filter(i => {
    return (mines.value.length > 0 ? mines.value.includes(i?.mine_id) : []) &&
      (processes.value.length > 0 ? processes.value.includes(i?.equipment_process_id) : []) &&
      (types.value.length > 0 ? types.value.includes(i?.equipment_type_id) : []);
  });
}

function filteredOperationList() {
  props.modelValue.forEach((group, groupIndex) => {
    Object.keys(group.mines).forEach((innerGroup) => {
      const currentSearchItem = findItem(props.modelValue[groupIndex].mines, props.modelValue[groupIndex].mines[innerGroup].name);
      if (isActiveFilter) {
        const operationsList = search.value ? searcherList.value[groupIndex][innerGroup]?.searcher?.search(search.value) : props.initialData[groupIndex].mines[innerGroup][props.operationType];
        currentSearchItem[props.operationType] = filterCurrentSearchItems(operationsList || []);
      } else {
        const currentItemSortFn = sortBy.value === 'alphabetical' ? byName.asc : byName.desc;
        currentSearchItem[props.operationType] = searchAndSortMine(search.value, groupIndex, innerGroup, currentItemSortFn);
      }
    });
  });
}

onMounted(load);

async function load() {
  buildSearcher();
}

function setFilterOptions(options) {
  mines.value = options.organisationMines;
  processes.value = options.organisationProcesses;
  types.value = options.organisationTypes;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => {
  if (!props.modelValue) return null;
  searchAndSort(search.value);
});

watch(() => props.initialData, buildSearcher);

function buildSearcher(items = props.initialData) {
  if (!items) return;
  (!isShaft.value && !isEquipment.value) && Object.keys(items).forEach((group) => {
    searcher[group] = new Searcher(items[group][props.operationType], { keys: searchKeys });
  });

  (isShaft.value || isEquipment.value) && items.forEach((group, groupIndex) => {
    searcherList.value[groupIndex] = [];
    Object.keys(group.mines).forEach((innerGroup) => {
      searcher[innerGroup] = new Searcher(group.mines[innerGroup][props.operationType], { keys: searchKeys });
      searcherList.value[groupIndex].push({ searcher: searcher[innerGroup] });
    });
  });
}

function findItem(list, name) {
  return list.find(item => item.name === name);
}

/**
 * Watch and set search
 */
watch(search, (value) => {
  searchAndSort(value);
});

function searchAndSort(value) {
  const currentItemSortFn = sortBy.value === 'alphabetical' ? byName.asc : byName.desc;

  (!isShaft.value && !isEquipment.value) && Object.keys(props.modelValue).forEach((group, index) => {
    const currentSearchItem = findItem(props.modelValue, props.initialData[index].name);
    currentSearchItem[props.operationType] = searchAndSortDivision(value, group, index, currentItemSortFn);
    currentSearchItem[props.operationType] = filterCurrentSearchItems(currentSearchItem[props.operationType]);
  });

  (isShaft.value || isEquipment.value) && props.modelValue.forEach((group, groupIndex) => {
    Object.keys(group.mines).forEach((innerGroup) => {
      const currentSearchItem = findItem(props.modelValue[groupIndex].mines, props.modelValue[groupIndex].mines[innerGroup].name);
      currentSearchItem[props.operationType] = searchAndSortMine(value, groupIndex, innerGroup, currentItemSortFn);
      currentSearchItem[props.operationType] = filterCurrentSearchItems(currentSearchItem[props.operationType]);
    });
  });
}

function filterCurrentSearchItems(list) {
  if (isActiveFilter) return filterItems(list);
  return list;
}

// For operation index pages with nested grouping 'division > operation'
function searchAndSortDivision(search, group, index, sortFn) {
  if (search) {
    return sortList(searcher[group]?.search(search), sortFn);
  } else {
    const currentListItem = findItem(props.initialData, props.initialData[index].name);
    return sortList(currentListItem[props.operationType], sortFn);
  }
}

// For operation index pages with nested grouping 'division > mine > operation'
function searchAndSortMine(search, outerGroup, innerGroup, sortFn) {
  if (search) {
    const searchList = searcherList.value[outerGroup][innerGroup]?.searcher?.search(search);
    return sortList(searchList, sortFn);
  } else {
    const currentListItem = findItem(props.initialData[outerGroup].mines, props.initialData[outerGroup].mines[innerGroup].name);
    return sortList(currentListItem[props.operationType], sortFn);
  }
}

function sortList(list, sortFn) {
  return sortBy.value ? list?.sort(sortFn) : list;
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.index-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;

  @include breakpoint(small) {
    flex-direction: column;
    background-color: map-get($background-color, darkest);
  }

  @include breakpoint(medium) {
    margin: 0 bu(2) bu(1);
    flex-direction: row;
    background-color: transparent;
  }

  @include breakpoint(large) {
    margin: 0 bu(6) bu(1);
  }

  &__search {
    @include breakpoint(small) {
      width: 100%;
    }

    @include breakpoint(medium) {
      width: bu(47);
    }
  }

  &__group {
    display: flex;
    justify-content: space-evenly;
    align-items: center;

    @include breakpoint(small) {
      width: 100%;
      margin: bu(.5) 0 bu(2);
    }

    @include breakpoint(medium) {
      width: auto;
      margin: 0;

      &--margin-right {
        margin: 0 bu(2) 0;
      }
    }
  }

  &__border {
    border-left: 1px solid map-get($foreground-color, darker);
    height: bu(2.5);
    margin: 0 bu(1) 0 bu(2);
  }
}
</style>
